
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { apiFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Loader2,
  AlertTriangle,
  Receipt,
  Calendar,
  Hash,
  Banknote,
  ChevronDown,
  Download,
  RefreshCw,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type ChargeRow = {
  id?: number | string;
  loanId?: number | string;
  credit?: number | string; // backend uses credit column
  created_at?: string;
  account?: string; // backend returns 'Late Fees'
};

type PaginationMeta = {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  has_more: boolean;
};

function safe(v: any, fallback = "-") {
  if (v === null || v === undefined || v === "") return fallback;
  return String(v);
}
function nf(v: any) {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString() : String(v);
}

export default function ActiveLoanChargesTab({ customerId }: { customerId: string }) {
  const perPage = 10;
  const location = useLocation();
  const nonce = (location.state as any)?.nonce;

  const [err, setErr] = useState("");
  const [infoMsg, setInfoMsg] = useState<string>("");

  const [rows, setRows] = useState<ChargeRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);

  const totalCharges = useMemo(
    () => rows.reduce((sum, r) => sum + (Number(r.credit) || 0), 0),
    [rows]
  );

  async function fetchCharges(p = 1) {
  if (!customerId) return;        // ✅ must be NOT customerId
  if (loading) return;

  setLoading(true);
  setErr("");

  try {
    const res = await apiFetch<any>(
      `/customer-profile/${customerId}/charges?page=${p}&per_page=${perPage}`
    );

    // apiFetch might return AxiosResponse OR already return payload
    const payload = res?.data ?? res;

    const rows = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
    const m = payload?.meta ?? null;

    //console.log("CHARGES payload:", payload);
    //console.log("CHARGES rows:", rows, "len:", rows.length);

    setRows(rows);
    setMeta(m);
    setPage(p);
  } catch (e: any) {
    setErr(e?.message || "Failed to load charges");
    setRows([]);
    setMeta(null);
  } finally {
    setLoading(false);
  }
}

  useEffect(() => {
    fetchCharges(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, nonce]);

  const hasMore = Boolean(meta?.has_more);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-sm font-semibold inline-flex items-center gap-2">
          <Receipt className="h-4 w-4 text-primary" />
          Charges
          {meta ? (
            <Badge className="bg-primary/10 text-primary border border-primary/20">
              {meta.total}
            </Badge>
          ) : null}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="secondary"
            onClick={() => fetchCharges(1)}
            disabled={loading}
            className="gap-2"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="gap-2" disabled={loading}>
                <Download className="h-4 w-4" />
                Download
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => alert("TODO: download charges PDF")}>PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={() => alert("TODO: download charges Excel")}>Excel</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {err ? (
        <Card className="shadow-card">
          <CardContent className="p-4 text-sm text-destructive">{err}</CardContent>
        </Card>
      ) : null}

      {infoMsg ? (
        <Card className="shadow-card">
          <CardContent className="p-4 text-sm text-muted-foreground flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5" />
            {infoMsg}
          </CardContent>
        </Card>
      ) : null}

      {/* Desktop table */}
      <div className="hidden lg:block overflow-x-auto rounded-xl border">
        <table className="min-w-[880px] w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="p-3">#</th>
              <th className="p-3">Charge Type</th>
              <th className="p-3">Loan ID</th>
              <th className="p-3">Amount</th>
              <th className="p-3">Created At</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="p-6 text-muted-foreground" colSpan={5}>
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading charges...
                  </div>
                </td>
              </tr>
            ) : rows.length ? (
              <>
                {rows.map((r, idx) => (
                  <tr key={String(r.id ?? idx)} className="border-t">
                    <td className="p-3">{idx + 1}</td>
                    <td className="p-3">
                      <span className="inline-flex items-center gap-2">
                        <Receipt className="h-4 w-4 text-primary" />
                        {safe(r.account, "Charge")}
                      </span>
                    </td>
                    <td className="p-3">{safe(r.loanId)}</td>
                    <td className="p-3 font-semibold">{nf(r.credit)}</td>
                    <td className="p-3">{safe(r.created_at)}</td>
                  </tr>
                ))}

                <tr className="border-t bg-muted/30">
                  <td className="p-3 font-semibold" colSpan={3}>
                    Total
                  </td>
                  <td className="p-3 font-semibold">{nf(totalCharges)}</td>
                  <td className="p-3" />
                </tr>
              </>
            ) : (
              <tr>
                <td className="p-4 text-muted-foreground" colSpan={5}>
                  No charges found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-3">
        {loading ? (
          <Card className="shadow-card">
            <CardContent className="p-4 text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading charges...
            </CardContent>
          </Card>
        ) : rows.length ? (
          <>
            {rows.map((r, idx) => (
              <Card key={String(r.id ?? idx)} className="shadow-card rounded-2xl border bg-background">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="h-8 w-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                          <Receipt className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{safe(r.account, "Charge")}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {safe(r.created_at)}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <div className="text-xs text-muted-foreground">Amount</div>
                      <div className="text-base font-bold text-primary">{nf(r.credit)}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl border bg-muted/20 p-3">
                      <div className="text-[11px] text-muted-foreground font-medium">Loan ID</div>
                      <div className="text-sm font-semibold inline-flex items-center gap-1">
                        <Hash className="h-3.5 w-3.5 text-muted-foreground" />
                        {safe(r.loanId)}
                      </div>
                    </div>

                    <div className="rounded-xl border bg-muted/20 p-3">
                      <div className="text-[11px] text-muted-foreground font-medium">Type</div>
                      <div className="text-sm font-semibold inline-flex items-center gap-1">
                        <Banknote className="h-3.5 w-3.5 text-muted-foreground" />
                        {safe(r.account, "Late Fees")}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <Card className="shadow-card">
              <CardContent className="p-3 flex items-center justify-between">
                <div className="text-sm font-semibold">Total</div>
                <div className="text-sm font-semibold">{nf(totalCharges)}</div>
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="text-sm text-muted-foreground">No charges found.</div>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs text-muted-foreground">
          {meta ? (
            <>
              Page {meta.current_page} of {meta.last_page} • Total {meta.total}
            </>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            disabled={loading || page <= 1}
            onClick={() => fetchCharges(Math.max(1, page - 1))}
          >
            Prev
          </Button>

          <Button
            variant="outline"
            disabled={loading || !hasMore}
            onClick={() => fetchCharges(page + 1)}
            className="gap-2"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}